/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import { alpha, Box, Button, Container, Divider, Grid, Skeleton, Typography, useTheme } from "@mui/material";
import { InfoOutlined as InfoOutlinedIcon } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "@store/store";
import { InactivityHandler } from "@components/InactivityHandler";
import { setLoanStatus } from "@store/slices/registerSlice";
import { STATUS_CATALOG, TAX_SYSTEM_TYPE } from "@helpers/constantes";
import {
  useLazyGetConditionsByIdQuery,
  useLazyGetLoanApplicationByIdQuery,
  usePostValidateNextStageMutation,
} from "@api/applications";
import { formatCurrency } from "@helpers/funciones";
import {
  useLazyGetAgriculturalCycleTypeConfigByIdQuery,
  useLazyGetHydrologicalRegimeTypeConfigByIdQuery,
  useLazyGetPropertyLocationConfigByPropertyLocationIdQuery,
  useLazyGetSowingTypeConfigBySowingTypeIdQuery,
} from "@api/catalogs";
import * as Sentry from "@sentry/react";
import { setModalErrorVisible } from "@store/slices/appSlice";
import { ApiError } from "@interfaces/index";
import { formatMaturityDate } from "./helpers";


const OfertaAvicola = () => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const application = useSelector((state: RootState) => state.register.application);
  const accountUser = useSelector((state: RootState) => state.register.account_user);

  const [getLoadApplication] = useLazyGetLoanApplicationByIdQuery();
  const [getConditions] = useLazyGetConditionsByIdQuery();
  const [getSowingTypeConfig] = useLazyGetSowingTypeConfigBySowingTypeIdQuery();
  const [getPropertyLocationConfig] = useLazyGetPropertyLocationConfigByPropertyLocationIdQuery();
  const [getAgriculturalCycleTypeConfig] = useLazyGetAgriculturalCycleTypeConfigByIdQuery();
  const [getHydrologicalRegimeTypeConfig] = useLazyGetHydrologicalRegimeTypeConfigByIdQuery();
  const [triggerPostNextStage] = usePostValidateNextStageMutation();

  const [hectares, setHectares] = useState(0);
  const [amount, setAmount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [summaryFields, setSummaryFields] = useState([
    { label: "Tasa anual", value: "" },
    { label: "Comisión administración", value: "" },
    { label: "Comisión disposición", value: "" },
    { label: "Aportación del productor", value: "" },
    { label: "Vencimiento", value: "" },
  ]);
  const [sowingType, setSowingType] = useState("");
  const [propertyLocation, setPropertyLocation] = useState("");
  const [agriculturalCycleType, setAgriculturalCycleType] = useState("");
  const [hydrologicalRegimeType, setHydrologicalRegimeType] = useState("");
  const [maturity_date, setMaturityDate] = useState("");

  const handleContinue = async () => {
    try {
      const { check_list, informacion_documentos_pfae } = STATUS_CATALOG;

      await triggerPostNextStage({
        applicationId: application.id,
        statusCode: check_list.code,
        username: `TOFU - ${accountUser.email}`,
      }).unwrap();

      dispatch(setLoanStatus(check_list.code));

      if (application.tax_system_type == TAX_SYSTEM_TYPE.PM.code) {
        navigate(check_list.url);
      } else {
        navigate(informacion_documentos_pfae.url);
      }
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          component: "OfertaAvicola",
        },
      });
      dispatch(setModalErrorVisible({ open: true, error: error as ApiError }));
    }
  };

  const mutedDark = alpha(theme.palette.primary.dark, 0.6);
  const borderColor = alpha(theme.palette.primary.dark, 0.12);

  useEffect(() => {
    const initialFetch = async () => {
      try {
        setIsLoading(true);

        const [conditions, loanApplication] = await Promise.all([
          getConditions(application.id).unwrap(),
          getLoadApplication(application.id).unwrap(),
        ]);

        console.log(conditions);

        let sowingType = "";
        let propertyLocation = "";
        let agriculturalCycleType = "";
        let hydrologicalRegimeType = "";

        if (loanApplication?.quoter_details?.sowing_type_id) {
          const responseSowingTypeConfig = await getSowingTypeConfig({
            sowing_type_id: loanApplication?.quoter_details?.sowing_type_id ?? "",
          }).unwrap();
          sowingType = responseSowingTypeConfig[0]?.sowing_type.name ?? "";
        }

        if (loanApplication?.quoter_details?.property_location_id) {
          const responsePropertyLocationConfig = await getPropertyLocationConfig({
            property_location_id: loanApplication?.quoter_details?.property_location_id ?? "",
          }).unwrap();
          propertyLocation = responsePropertyLocationConfig[0]?.property_location.name ?? "";
        }

        if (loanApplication?.quoter_details?.agricultural_cycle_type_id) {
          const responseAgriculturalCycleTypeConfig = await getAgriculturalCycleTypeConfig({
            agricultural_cycle_type_id: loanApplication?.quoter_details?.agricultural_cycle_type_id ?? "",
          }).unwrap();
          agriculturalCycleType = responseAgriculturalCycleTypeConfig[0]?.agricultural_cycle_type.name ?? "";
        }
        if (loanApplication?.quoter_details?.hydrological_regime_type_id) {
          const responseHydrologicalRegimeTypeConfig = await getHydrologicalRegimeTypeConfig({
            hydrological_regime_type_id: loanApplication?.quoter_details?.hydrological_regime_type_id ?? "",
          }).unwrap();
          hydrologicalRegimeType = responseHydrologicalRegimeTypeConfig[0]?.hydrological_regime_type.name ?? "";
        }

        setHectares(loanApplication?.quoter_details?.hectares ?? 0);
        setAmount(conditions[0]?.approved_amount ?? 0);
        setSowingType(sowingType);
        setPropertyLocation(propertyLocation);
        setAgriculturalCycleType(agriculturalCycleType);
        setHydrologicalRegimeType(hydrologicalRegimeType);

        const textoTasa = conditions[0]?.extra_conditions?.interest_type === "VARIABLE" ? "TIIE 28d +" : "";
        const producer_contribution = formatCurrency(conditions[0]?.extra_conditions?.producer_contribution || 0);
        const maturity_date_fomat = formatMaturityDate(conditions[0]?.extra_conditions?.maturity_date ?? "");

        setSummaryFields([
          { label: "Tasa anual", value: `${textoTasa} ${conditions[0]?.interest_rate?.toFixed(0) || "0"}%` },
          { label: "Comisión administración", value: `${conditions[0]?.administration_fee?.toFixed(0) || "0"}% + IVA` },
          {
            label: "Comisión disposición",
            value: loanApplication?.opening_fee != null ? `${loanApplication.opening_fee.toFixed(0)}% + IVA` : "",
          },
          { label: "Aportación del productor", value: producer_contribution },
          { label: "Vencimiento", value: maturity_date_fomat },
        ]);
        setMaturityDate(maturity_date_fomat);
      } catch (error) {
        Sentry.captureException(error, {
          extra: {
            component: "OfertaAvicola - initialFetch",
          },
        });
        dispatch(setModalErrorVisible({ open: true, error: error as ApiError }));
      } finally {
        setIsLoading(false);
      }
    };

    initialFetch();
  }, []);

  return (
    <Grid container>
      <Grid item xs={12} md={12} mt={5} sx={{ position: "relative" }}>
        <InactivityHandler />
        <Container maxWidth={"sm"}>
          {isLoading ? (
            <Box sx={{ mx: 2, mb: 6 }}>
              <Skeleton variant="text" width={180} height={28} sx={{ mb: 1.5 }} />
              <Skeleton variant="text" width="90%" height={44} sx={{ mb: 1.5 }} />
              <Skeleton variant="text" height={24} sx={{ mb: 0.5 }} />
              <Skeleton variant="text" width="70%" height={24} sx={{ mb: 4 }} />

              <Skeleton variant="rounded" height={230} sx={{ borderRadius: "20px", mb: 2.5 }} />
              <Skeleton variant="rounded" height={280} sx={{ borderRadius: "20px", mb: 2.5 }} />
              <Skeleton variant="rounded" height={84} sx={{ borderRadius: "14px", mb: 3 }} />

              <Skeleton variant="rounded" width={220} height={44} sx={{ borderRadius: "8px" }} />
            </Box>
          ) : (
            <Box sx={{ mx: 2, mb: 6 }}>
              <Box display="flex" alignItems="center" gap={1} mb={1.5}>
                <Box width={4} height={16} bgcolor="primary.main" borderRadius={1} />
                <Typography
                  variant="overline"
                  color="primary.main"
                  fontWeight={theme?.palette?.fontWeights?.bold}
                  sx={{ letterSpacing: "0.5px" }}
                >
                  Decisión · Pre-autorización
                </Typography>
              </Box>

              <Typography
                variant="h4"
                color="primary.dark"
                fontWeight={theme?.palette?.fontWeights?.bold}
                fontSize={{ xs: "24px", sm: "28px" }}
                mb={1.5}
              >
                ¡Tu crédito de avío está pre-autorizado!
              </Typography>

              <Typography variant="body1" color="primary.dark" sx={{ opacity: 0.75, lineHeight: 1.6 }} mb={4}>
                Autorizamos financiamiento para{" "}
                <Box component="span" fontWeight={theme?.palette?.fontWeights?.semibold}>
                  {sowingType}
                </Box>{" "}
                en{" "}
                <Box component="span" fontWeight={theme?.palette?.fontWeights?.semibold}>
                  {propertyLocation}
                </Box>
                , ciclo{" "}
                <Box component="span" fontWeight={theme?.palette?.fontWeights?.semibold}>
                  {agriculturalCycleType}
                </Box>{" "}
                · {hydrologicalRegimeType}. Ajusta la superficie a financiar dentro del rango autorizado.
              </Typography>

              <Box
                sx={{
                  background: `linear-gradient(180deg, ${alpha(theme.palette.primary.base, 0.9)} 0%, ${alpha(theme.palette.primary.base, 0.35)} 100%)`,
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.25)}`,
                  borderRadius: "20px",
                  p: { xs: 2.5, sm: 3.5 },
                  mb: 2.5,
                }}
              >
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} gap={2}>
                  <Typography
                    variant="body2"
                    fontWeight={theme?.palette?.fontWeights?.bold}
                    sx={{ color: mutedDark, textTransform: "uppercase", letterSpacing: "0.4px" }}
                  >
                    Monto de crédito · {hectares} hectáreas
                  </Typography>

                  <Box
                    display="flex"
                    alignItems="center"
                    gap={0.75}
                    sx={{ bgcolor: "common.white", borderRadius: "999px", px: 1.5, py: 0.5, flexShrink: 0 }}
                  >
                    <Box width={7} height={7} borderRadius="50%" bgcolor="primary.main" />
                    <Typography variant="caption" fontWeight={theme?.palette?.fontWeights?.bold} color="primary.main">
                      Pre-autorizado
                    </Typography>
                  </Box>
                </Box>

                <Typography component="div" color="primary.dark" fontWeight={theme?.palette?.fontWeights?.bold} mb={1}>
                  <Box component="span" sx={{ fontSize: { xs: "32px", sm: "38px" }, lineHeight: 1 }}>
                    {formatCurrency(amount)}
                  </Box>{" "}
                  <Box component="span" sx={{ fontSize: "14px", fontWeight: 500 }}>
                    MXN
                  </Box>
                </Typography>

                <Typography variant="body2" mb={2.5} sx={{ color: mutedDark }}>
                  Capital + intereses al vencimiento · {maturity_date} · Sujeto a dictamen técnico del predio
                </Typography>

                <Divider sx={{ borderColor: alpha(theme.palette.primary.dark, 0.15), mb: 2.5 }} />

                <Grid container rowSpacing={2.5} columnSpacing={2}>
                  {summaryFields.map((field) => (
                    <Grid item xs={6} key={field.label}>
                      <Typography
                        variant="caption"
                        sx={{ color: mutedDark, textTransform: "uppercase", letterSpacing: "0.3px", display: "block" }}
                      >
                        {field.label}
                      </Typography>
                      <Typography fontWeight={theme?.palette?.fontWeights?.semibold} color="primary.dark">
                        {field.value}
                      </Typography>
                    </Grid>
                  ))}
                </Grid>
              </Box>

              <Box
                sx={{
                  bgcolor: alpha(theme.palette.primary.base, 0.6),
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                  borderRadius: "14px",
                  p: 2,
                  display: "flex",
                  gap: 1.25,
                  mb: 3,
                }}
              >
                <InfoOutlinedIcon sx={{ color: "primary.main", fontSize: 20, mt: "1px", flexShrink: 0 }} />
                <Typography variant="body2" color="primary.dark" sx={{ opacity: 0.85, lineHeight: 1.55 }}>
                  Esta pre-autorización no constituye una autorización definitiva del crédito. Está sujeta al
                  cumplimiento de todos los requisitos de formalización y contratación, y las condiciones podrían
                  cambiar según el dictamen técnico del predio y la situación vigente al momento de la contratación.
                </Typography>
              </Box>

              <Divider sx={{ borderColor, mb: 2.5 }} />

              <Box display="flex" alignItems="flex-end" justifyContent="flex-end" gap={1.5}>
                <Button variant="default" onClick={handleContinue}>
                  Continuar al expediente
                </Button>
              </Box>
            </Box>
          )}
        </Container>
      </Grid>
    </Grid>
  );
};

export default OfertaAvicola;
